import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Trophy, Award, Users, Loader2, Crown, ArrowRight, Shield,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicCoachProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Deliberately DO NOT send the caller's auth token — this must
        // resolve as a public request even for logged-in users viewing
        // their own share link.
        const response = await axios.get(`${API}/coach/public/${slug}`, {
          transformRequest: [(d, headers) => {
            if (headers) delete headers.Authorization;
            return d;
          }],
        });
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="public-coach-profile-not-found">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2 font-title">Profile not available</h1>
          <p className="text-gray-500 text-sm mb-6">
            This coach profile is either private or doesn&apos;t exist. Ask the coach
            for an updated link.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2"
          >
            Explore Grassroots Coaching <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </div>
    );
  }

  const {
    coach_name, current_role, team_name, club_badge, profile_photo,
    personal_bio, coach_qualifications, total_stats, season_stats,
  } = data;

  const resolveSrc = (src) =>
    src?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
  const photoSrc = profile_photo || club_badge;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-coach-profile">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Header banner */}
        <Card
          className="text-white overflow-hidden border-transparent"
          style={{ background: 'linear-gradient(to right, #064E3B, #052e1f)' }}
        >
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {photoSrc ? (
                  <img
                    src={resolveSrc(photoSrc)}
                    alt={coach_name}
                    className="w-16 h-16 rounded-2xl object-cover"
                    data-testid="public-coach-photo"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Crown className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                )}
                <div className="leading-tight">
                  <h1 className="text-2xl sm:text-3xl font-bold" data-testid="public-coach-name">{coach_name}</h1>
                  {current_role && <p className="text-white/85 text-sm -mt-0.5">{current_role}</p>}
                  {team_name && <p className="text-emerald-300 text-sm font-semibold mt-1">{team_name}</p>}
                </div>
              </div>
              <div className="flex gap-6 sm:gap-8 text-center">
                <div>
                  <p className="text-3xl sm:text-4xl font-bold">{total_stats?.total_seasons ?? 0}</p>
                  <p className="text-white/70 text-sm">Seasons</p>
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl font-bold">{total_stats?.matches_played ?? 0}</p>
                  <p className="text-white/70 text-sm">Matches</p>
                </div>
                <div>
                  <p className="text-3xl sm:text-4xl font-bold">{total_stats?.win_percentage ?? 0}%</p>
                  <p className="text-white/70 text-sm">Win Rate</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        {(personal_bio || coach_qualifications) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                About the Coach
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {personal_bio && (
                <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {personal_bio}
                </p>
              )}
              {coach_qualifications && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5" strokeWidth={2} /> Qualifications
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {coach_qualifications.split(/[,;\n]/).map((q) => q.trim()).filter(Boolean).map((q, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="rounded-full px-3 py-1 text-xs bg-emerald-50/60 border-emerald-200 text-emerald-800"
                      >
                        {q}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Season-by-Season */}
        {Array.isArray(season_stats) && season_stats.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                <Trophy className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                Season-by-Season
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-gray-500 border-b">
                    <tr>
                      <th className="text-left py-2 pr-4">Season</th>
                      <th className="py-2 px-2">P</th>
                      <th className="py-2 px-2">W</th>
                      <th className="py-2 px-2">D</th>
                      <th className="py-2 px-2">L</th>
                      <th className="py-2 px-2">GF</th>
                      <th className="py-2 px-2">GA</th>
                      <th className="py-2 px-2">Pts</th>
                      <th className="py-2 pl-2">Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {season_stats.map((s) => (
                      <tr key={s.id} className="border-b last:border-0 text-gray-700">
                        <td className="py-2 pr-4 font-semibold text-gray-900">{s.name}</td>
                        <td className="py-2 px-2 text-center">{s.matches_played}</td>
                        <td className="py-2 px-2 text-center text-emerald-700 font-semibold">{s.wins}</td>
                        <td className="py-2 px-2 text-center">{s.draws}</td>
                        <td className="py-2 px-2 text-center text-red-500">{s.losses}</td>
                        <td className="py-2 px-2 text-center">{s.goals_scored}</td>
                        <td className="py-2 px-2 text-center">{s.goals_conceded}</td>
                        <td className="py-2 px-2 text-center font-semibold">{s.points}</td>
                        <td className="py-2 pl-2 text-center">{s.win_percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Powered-by banner (conversion) */}
        <Card
          className="border-transparent overflow-hidden"
          style={{ background: 'linear-gradient(to right, #ecfdf5, #d1fae5)' }}
          data-testid="public-coach-powered-by"
        >
          <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-900">
                Coach your team with Grassroots Coaching
              </p>
              <p className="text-xs text-emerald-800/80">
                Free-to-start platform for youth football coaches. Track matches, stats, training and your own coaching CV.
              </p>
            </div>
            <Link
              to={`/?ref=${encodeURIComponent(slug)}`}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 shrink-0"
              data-testid="public-coach-cta"
            >
              Try free <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
